import { ref, computed } from 'vue'
import type { Document } from '@/types'

const STORAGE_KEY = 'document-viewer-state'

interface PersistedState {
  docId: string
  mode: 'open' | 'minimized'
}

// Singleton state (module-level)
const isOpen = ref(false)
const isMinimized = ref(false)
const currentDocumentId = ref<string | null>(null)
const currentDocument = ref<Document | null>(null)
const minimizedDocuments = ref<Document[]>([])
const editRequestedDoc = ref<Document | null>(null)

function persist(state: PersistedState | null) {
  try {
    if (state) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state))
    } else {
      localStorage.removeItem(STORAGE_KEY)
    }
  } catch { /* ignore */ }
}

function loadPersisted(): PersistedState | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    return JSON.parse(raw)
  } catch { return null }
}

export function useDocumentViewer() {
  function open(doc: Document) {
    currentDocument.value = doc
    currentDocumentId.value = doc.id
    isOpen.value = true
    isMinimized.value = false
    persist({ docId: doc.id, mode: 'open' })
  }

  function openById(docId: string) {
    currentDocumentId.value = docId
    currentDocument.value = null // will be fetched by the panel
    isOpen.value = true
    isMinimized.value = false
    persist({ docId, mode: 'open' })
  }

  function close() {
    isOpen.value = false
    isMinimized.value = false
    currentDocumentId.value = null
    currentDocument.value = null
    persist(null)
  }

  function minimize() {
    if (currentDocument.value) {
      const already = minimizedDocuments.value.find(d => d.id === currentDocument.value!.id)
      if (!already) {
        minimizedDocuments.value.push(currentDocument.value)
      }
      persist({ docId: currentDocument.value.id, mode: 'minimized' })
    }
    isOpen.value = false
    isMinimized.value = true
  }

  function restoreFromMinimized(docId: string) {
    const doc = minimizedDocuments.value.find(d => d.id === docId)
    if (doc) {
      minimizedDocuments.value = minimizedDocuments.value.filter(d => d.id !== docId)
      open(doc)
    }
  }

  function removeFromMinimized(docId: string) {
    minimizedDocuments.value = minimizedDocuments.value.filter(d => d.id !== docId)
    if (minimizedDocuments.value.length === 0) {
      isMinimized.value = false
    }
    const persisted = loadPersisted()
    if (persisted?.docId === docId) {
      persist(null)
    }
  }

  function setCurrentDocument(doc: Document) {
    currentDocument.value = doc
  }

  function requestEdit(doc: Document) {
    editRequestedDoc.value = doc
    close()
  }

  function clearEditRequest() {
    editRequestedDoc.value = null
  }

  function addToMinimized(doc: Document) {
    const already = minimizedDocuments.value.find(d => d.id === doc.id)
    if (!already) {
      minimizedDocuments.value.push(doc)
    }
  }

  function initFromStorage() {
    const state = loadPersisted()
    if (!state || isOpen.value) return

    if (state.mode === 'open') {
      openById(state.docId)
    } else if (state.mode === 'minimized') {
      // Restore as minimized — fetch doc and add to minimized list
      currentDocumentId.value = state.docId
      isMinimized.value = true
    }
  }

  return {
    // State
    isOpen: computed(() => isOpen.value),
    isMinimized: computed(() => isMinimized.value),
    currentDocumentId: computed(() => currentDocumentId.value),
    currentDocument: computed(() => currentDocument.value),
    minimizedDocuments: computed(() => minimizedDocuments.value),
    // Actions
    open,
    openById,
    close,
    minimize,
    restoreFromMinimized,
    removeFromMinimized,
    editRequestedDoc: computed(() => editRequestedDoc.value),
    setCurrentDocument,
    addToMinimized,
    requestEdit,
    clearEditRequest,
    initFromStorage,
  }
}
